import { useMemo } from "react";
import {
  DollarSign,
  AlertTriangle,
  CreditCard,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

// Types
interface Customer {
  id: string;
  name: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  lastPayment: string;
  paymentTerms: "Net 15" | "Net 30" | "Net 45" | "Net 60" | "Net 90";
}

interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: "overdue" | "outstanding" | "paid" | "partially_paid";
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  invoiceDate?: string;
}

interface CustomerMetricCardsProps {
  customer: Customer;
  invoices: Invoice[];
  payments: Payment[];
  onCardClick: (action: "outstanding" | "overdue" | "credit" | "performance") => void;
}

// Helper function to format currency with 2 decimal places
const formatCurrencyDetailed = (value: number, currency: string): string => {
  const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    CAD: "CA$",
    AUD: "A$",
  };

  const symbol = currencySymbols[currency] || "$";
  return `${symbol}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Helper function to format date
const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function CustomerMetricCards({
  customer,
  invoices,
  payments,
  onCardClick,
}: CustomerMetricCardsProps) {
  // Calculate metrics
  const metrics = useMemo(() => {
    const today = new Date();

    // Outstanding Balance Calculation
    const outstandingInvoices = invoices.filter(
      (inv) => inv.status === "outstanding" || inv.status === "partially_paid"
    );
    const outstandingBalance = outstandingInvoices.reduce(
      (sum, inv) => sum + inv.amount,
      0
    );
    const outstandingCount = outstandingInvoices.length;

    // Overdue Balance Calculation
    const overdueInvoices = invoices.filter((inv) => {
      if (inv.status === "paid") return false;
      const dueDate = new Date(inv.dueDate);
      return dueDate < today;
    });
    const overdueBalance = overdueInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    const overdueCount = overdueInvoices.length;

    // Credit Available Calculation
    const availableCredit = customer.creditLimit - outstandingBalance;
    const creditUsedPercentage =
      customer.creditLimit > 0
        ? (outstandingBalance / customer.creditLimit) * 100
        : 0;

    // Payment Performance Calculation
    const paidInvoicesWithPayments = payments
      .filter((payment) => {
        if (!payment.invoiceDate) return false;
        const paymentDate = new Date(payment.date);
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        return paymentDate >= twelveMonthsAgo;
      })
      .map((payment) => {
        const invoiceDate = new Date(payment.invoiceDate!);
        const paymentDate = new Date(payment.date);
        const daysDiff = Math.floor(
          (paymentDate.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysDiff;
      });

    const averagePaymentDays =
      paidInvoicesWithPayments.length > 0
        ? Math.round(
            paidInvoicesWithPayments.reduce((sum, days) => sum + days, 0) /
              paidInvoicesWithPayments.length
          )
        : customer.dso;

    // Last payment date
    const sortedPayments = [...payments].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const lastPaymentDate =
      sortedPayments.length > 0 ? sortedPayments[0].date : customer.lastPayment;

    return {
      outstandingBalance,
      outstandingCount,
      overdueBalance,
      overdueCount,
      availableCredit,
      creditUsedPercentage,
      averagePaymentDays,
      lastPaymentDate,
    };
  }, [customer, invoices, payments]);

  // Get progress bar accent based on credit usage
  const getCreditProgressAccent = (
    percentage: number
  ): "success" | "warning" | "destructive" => {
    if (percentage >= 80) return "destructive";
    if (percentage >= 50) return "warning";
    return "success";
  };

  // Extract payment terms number
  const paymentTermsNumber = customer.paymentTerms.replace("Net ", "");

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Outstanding Balance Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-orange-500"
        onClick={() => onCardClick("outstanding")}
        role="button"
        tabIndex={0}
        aria-label={`Outstanding Balance: ${formatCurrencyDetailed(
          metrics.outstandingBalance,
          customer.currency
        )}. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("outstanding");
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-full bg-orange-100">
                  <DollarSign className="h-4 w-4 text-orange-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Outstanding Balance
              </p>
              <p className="text-2xl font-bold">
                {formatCurrencyDetailed(metrics.outstandingBalance, customer.currency)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {metrics.outstandingCount}{" "}
                {metrics.outstandingCount === 1
                  ? "outstanding invoice"
                  : "outstanding invoices"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overdue Balance Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-red-500"
        onClick={() => onCardClick("overdue")}
        role="button"
        tabIndex={0}
        aria-label={`Overdue Balance: ${formatCurrencyDetailed(
          metrics.overdueBalance,
          customer.currency
        )}. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("overdue");
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-full bg-red-100">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Overdue Balance
              </p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrencyDetailed(metrics.overdueBalance, customer.currency)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {metrics.overdueCount === 0
                  ? "No overdue invoices"
                  : `${metrics.overdueCount} ${
                      metrics.overdueCount === 1 ? "overdue invoice" : "overdue invoices"
                    }`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Available Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-green-500"
        onClick={() => onCardClick("credit")}
        role="button"
        tabIndex={0}
        aria-label={`Credit Available: ${formatCurrencyDetailed(
          metrics.availableCredit,
          customer.currency
        )}. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("credit");
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-full bg-green-100">
                  <CreditCard className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Credit Available
              </p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrencyDetailed(metrics.availableCredit, customer.currency)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Limit: {formatCurrencyDetailed(customer.creditLimit, customer.currency)} (
                {metrics.creditUsedPercentage.toFixed(1)}% used)
              </p>
              <div className="mt-3">
                <Progress
                  value={metrics.creditUsedPercentage}
                  className="h-2"
                  accent={getCreditProgressAccent(metrics.creditUsedPercentage)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Performance Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-blue-500"
        onClick={() => onCardClick("performance")}
        role="button"
        tabIndex={0}
        aria-label={`Payment Performance: ${metrics.averagePaymentDays} days average. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("performance");
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-full bg-blue-100">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Payment Performance
              </p>
              <p className="text-2xl font-bold">{metrics.averagePaymentDays} days</p>
              <p className="text-xs text-muted-foreground mt-2">
                Average payment time • Terms: Net {paymentTermsNumber}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Last payment: {formatDateShort(metrics.lastPaymentDate)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
